import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, Grid } from '@material-ui/core';
import { Visibility as VisibilityIcon } from '@material-ui/icons';

import { FetchError } from '../../common/api/api';
import HoveredCard from '../../common/card/HoveredCard';
import { Task } from '../tasks/Task.interface';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import TaskStatusVersionCard from '../tasks/cards/TaskStatusVersionCard';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
// import HazardDescriptionCard from './cards/HazardDescriptionCard';
import HazardInfoCard from './cards/HazardInfoCard';
import HazardInitialAssessmentCard from './cards/HazardInitialAssessmentCard';
import HazardPeopleCard from './cards/HazardPeopleCard';
import HazardResidualAssessmentCard from './cards/HazardResidualAssessmentCard';
import { BaseHazardCardProps, Hazard, HazardVersion } from './Hazard.interface';
import HazardImpactCard from './cards/HazardImpactCard';
import HazardReferenceCard from './cards/HazardReferenceCard';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import DeleteEntityCard from '../../common/cards/DeleteEntityCard';
import { useUserContext } from '../../user/User.context';
import { isSectionEnabled } from '../../common/checkSectionAvailability';

export interface HazardDetailPageProps {
  hazard: Hazard;
  productId: number;
  hazardVersion?: HazardVersion;
  onChange?: (hazard: Partial<Hazard>) => void;
  onError?: (error: FetchError) => void;
}

const HazardDetailPage: React.FC<HazardDetailPageProps> = props => {
  const { hazard, hazardVersion, productId, onChange, onError } = props;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const { hasRole } = useUserContext();
  const { isFieldDisabled } = useTaskEntityFields(hazard.task);
  const disabled = !!hazardVersion;
  const task = hazardVersion?.task || hazard.task;

  const handleChange = (hazard: Hazard) => {
    // notify audit log to refresh
    hazard.task = { ...hazard.task };
    onChange?.(hazard);
  };

  const handleTaskUpdate = (newTask: Partial<Task>) => {
    if (!hazard?.task) {
      return;
    }
    Object.assign(hazard.task, newTask);
    handleChange({ ...hazard });
  };

  const cardProps: BaseHazardCardProps = {
    hazard: hazard!,
    hazardVersion,
    onChange: handleChange
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <HazardInfoCard {...cardProps} />
          </Grid>
          {/* <Grid item xs={12}>
            <HazardDescriptionCard {...cardProps} />
          </Grid> */}
          <Grid item xs={12}>
            <HazardImpactCard {...cardProps} />
          </Grid>
          <Grid item xs={12}>
            <HazardInitialAssessmentCard {...cardProps} />
          </Grid>
          <Grid item xs={12}>
            <HazardResidualAssessmentCard {...cardProps} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={4}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TaskStatusVersionCard
              taskEntity={hazard}
              renderContent={group =>
                group.version !== hazard.task.version && (
                  <HoveredCard
                    title={`Version ${group.version}`}
                    content='View the previous version'
                    actions={
                      <Button
                        variant='text'
                        color='primary'
                        component={Link}
                        startIcon={<VisibilityIcon />}
                        to={`/portal/products/${productId}/hazards/${hazard.id}/detail?ver=${group.version}`}
                      >
                        View
                      </Button>
                    }
                  />
                )
              }
            />
          </Grid>
          <Grid item xs={12}>
            <HazardPeopleCard {...cardProps} />
          </Grid>
          {task?.children && task.children.length > 0 && (
            <Grid item xs={12}>
              <SubTaskListCard
                title='Stakeholders'
                task={task}
                disabled={disabled || isFieldDisabled('assignSubTasks')}
                onError={onError}
                onChange={handleTaskUpdate}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TaskFileListCard
              task={task}
              disabled={disabled}
              fileReferences={hazardVersion?.fileIds}
              onError={onError}
              onChange={handleTaskUpdate}
            />
          </Grid>
          {isSectionEnabled('Products') && (
            <>
              <Grid item xs={12}>
                <HazardReferenceCard
                  hazardId={hazard.id}
                  productId={productId}
                  target='user-needs'
                  version={hazardVersion?.version}
                  disabled={disabled || isFieldDisabled('assignUserNeeds')}
                />
              </Grid>
              <Grid item xs={12}>
                <HazardReferenceCard
                  hazardId={hazard.id}
                  productId={productId}
                  target='design-inputs'
                  version={hazardVersion?.version}
                  disabled={disabled || isFieldDisabled('assignDesignInputs')}
                />
              </Grid>
            </>
          )}
          {!disabled && hasRole('Delete') && (
            <Grid item xs={12}>
              <DeleteEntityCard
                title='Hazard'
                apiPath={`hazards`}
                entity={hazard}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default HazardDetailPage;
